import type { ProjectTask, TimberProject } from '../types';
import { buildGanttDays, computeGanttDateRangeMs } from './ganttDateRange';
import { findOverlappingTaskPairs, stripTime } from './taskSchedule';

export interface ShopScheduleViewModel {
  filteredTasks: ProjectTask[];
  projectsOrdered: TimberProject[];
  overlapPairs: Array<{ a: ProjectTask; b: ProjectTask }>;
  overlappingTaskIds: Set<string>;
  dateRange: { start: Date; end: Date };
  days: Date[];
  rangeStartMs: number;
  tasksByProject: Map<string, ProjectTask[]>;
  dayIndex: (d: Date) => number;
  stats: {
    productionCount: number;
    installationCount: number;
    overlapPairCount: number;
    overlappingTaskCount: number;
    projectCount: number;
    tasksStartingThisWeek: number;
    tasksEndingThisWeek: number;
  };
}

function startOfWeek(d: Date): Date {
  const x = stripTime(new Date(d));
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function endOfWeek(d: Date): Date {
  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return e;
}

/**
 * Shared data for shop schedule UIs (normal + big-screen).
 */
export function getShopScheduleViewModel(
  projectTasks: ProjectTask[],
  timberProjects: TimberProject[],
): ShopScheduleViewModel {
  const filteredTasks = projectTasks.filter(
    (t) => t.department === 'production' || t.department === 'installation',
  );

  const ids = new Set(filteredTasks.map((t) => t.projectId));
  const projectsOrdered = timberProjects
    .filter((p) => p.id && ids.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const overlapPairs = findOverlappingTaskPairs(filteredTasks, () => true);
  const overlappingTaskIds = new Set<string>();
  overlapPairs.forEach(({ a, b }) => {
    if (a.id) overlappingTaskIds.add(a.id);
    if (b.id) overlappingTaskIds.add(b.id);
  });

  const range = computeGanttDateRangeMs({
    taskTimes: filteredTasks.flatMap((t) => [t.startDate, t.endDate]),
  });
  const dateRange = { start: new Date(range.startMs), end: new Date(range.endMs) };
  const days = buildGanttDays(range.startMs, range.endMs);

  const rangeStartMs = stripTime(new Date(dateRange.start)).getTime();

  const dayIndex = (d: Date) => Math.round((stripTime(d).getTime() - rangeStartMs) / 86400000);

  const tasksByProject = new Map<string, ProjectTask[]>();
  filteredTasks.forEach((t) => {
    if (!tasksByProject.has(t.projectId)) tasksByProject.set(t.projectId, []);
    tasksByProject.get(t.projectId)!.push(t);
  });

  const now = new Date();
  const wkStart = startOfWeek(now).getTime();
  const wkEnd = endOfWeek(now).getTime();
  let tasksStartingThisWeek = 0;
  let tasksEndingThisWeek = 0;
  filteredTasks.forEach((t) => {
    const s = stripTime(new Date(t.startDate)).getTime();
    const e = stripTime(new Date(t.endDate)).getTime();
    if (s >= wkStart && s <= wkEnd) tasksStartingThisWeek++;
    if (e >= wkStart && e <= wkEnd) tasksEndingThisWeek++;
  });

  const productionCount = filteredTasks.filter((t) => t.department === 'production').length;
  const installationCount = filteredTasks.filter((t) => t.department === 'installation').length;

  return {
    filteredTasks,
    projectsOrdered,
    overlapPairs,
    overlappingTaskIds,
    dateRange,
    days,
    rangeStartMs,
    tasksByProject,
    dayIndex,
    stats: {
      productionCount,
      installationCount,
      overlapPairCount: overlapPairs.length,
      overlappingTaskCount: overlappingTaskIds.size,
      projectCount: projectsOrdered.length,
      tasksStartingThisWeek,
      tasksEndingThisWeek,
    },
  };
}
